"use client";

// AuthCallback — der Rueckweg aus der Bestaetigungsmail (`/auth/callback`).
//
// Bis 2026-08-19 lag diese Seite als 16 handgeschriebene Kopien in den Apps, in
// SECHS Fassungen (R4-01 · S2). Sie war die einzige der vier Auth-Seiten ohne
// Vorlage — und die einzige mit Drift; Login, Reset und Update sind ueber
// `AuthOnboarding entry=` in 17 von 17 Apps identisch.
//
// Was hier steht, ist die VEREINIGUNG der reifsten Fassungen, nicht die
// haeufigste: meets Auswertung von `uebernimmSitzungAusHash` (Fehlerursache statt
// Einheitstext, keine Warteschleife) plus notes'/brains Doppelmount-Riegel und
// `?next=`-Behandlung. Die uebrigen 14 Apps erben beides mit dem Umzug.

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { uebernimmSitzungAusHash } from "@janeway/app-sdk/auth/implizite-sitzung";
import { isSafeRedirect } from "../../../lib/utils";
import { ErrorMessage } from "../../ui/ErrorMessage";
import { Button } from "../../ui/Button";

/** Nur der Teil des Supabase-Clients, den der Rueckweg anfasst — haelt den Test schlank. */
export interface CallbackClient {
  auth: {
    exchangeCodeForSession(code: string): Promise<{ error: { message: string } | null }>;
    setSession(t: { access_token: string; refresh_token: string }): Promise<{ error: { message: string } | null }>;
    getSession(): Promise<{ data: { session: unknown } }>;
  };
}

export interface CallbackErgebnis {
  /** Wohin weitergeleitet wird. */
  ziel: string;
  /** Gesetzt: erst diese Meldung zeigen, dann nach `ziel`. */
  fehler: string | null;
}

/** Wen ein Fragment anmelden wuerde — fuer die Rueckfrage (ST-22). */
export interface FremdesKonto {
  email: string | null;
}

/** Abstand zum `exp`-Vergleich in auth-js `_setSession` (Lock, Uhr, Bedenkzeit). */
const EXP_RESERVE_S = 30;

/** `email` und `exp` aus der Nutzlast, ungeprueft — nur zum Anzeigen und Vergleichen. */
function leseToken(token: string): { email: string | null; exp: number | null } {
  try {
    const teil = token.split(".")[1];
    if (!teil) return { email: null, exp: null };
    const { email, exp } = JSON.parse(
      new TextDecoder().decode(Uint8Array.from(atob(teil.replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0))),
    ) as { email?: unknown; exp?: unknown };
    return {
      email: typeof email === "string" ? email : null,
      exp: typeof exp === "number" && Number.isFinite(exp) ? exp : null,
    };
  } catch {
    return { email: null, exp: null };
  }
}

// Nur bei `exp` in der Zukunft prueft `setSession` die Signatur (`_getUser`);
// sonst tauscht es ungeprueft den refresh_token, und die Adresse, nach der wir
// gefragt haben, stammte aus einem beliebig gefaelschten Token.
function nochGueltig(token: string): boolean {
  const { exp } = leseToken(token);
  return exp !== null && exp > Date.now() / 1000 + EXP_RESERVE_S;
}

/**
 * Entscheidet, wohin ein Klick auf den Mail-Link fuehrt. Rein und ohne DOM —
 * die Huelle ist ein Spinner, hier faellt die Entscheidung ueber die Anmeldung.
 */
export async function loeseAuthCallbackEin(
  supabase: CallbackClient,
  url: { search: string; hash: string },
  ziele: { dashboard: string; login: string },
  /**
   * Rueckfrage vor der Uebernahme eines Fragments ohne bestehende Sitzung (ST-22):
   * `setSession` prueft nur, dass das Token gueltig ist, nicht wessen es ist — ein
   * praeparierter Link meldete sonst still am Konto des Absenders an.
   */
  bestaetige: (konto: FremdesKonto) => Promise<boolean>,
): Promise<CallbackErgebnis> {
  // Das Fragment ZUERST lesen — sobald der Supabase-Client anlaeuft, ist es weg.
  const rawHash = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
  const hashType = new URLSearchParams(rawHash).get("type");

  const params = new URLSearchParams(url.search);
  const code = params.get("code");
  const errorDesc = params.get("error_description");
  const nextParam = params.get("next");

  if (errorDesc) {
    return { ziel: `${ziele.login}?error=${encodeURIComponent(errorDesc)}`, fehler: null };
  }

  if (code) {
    const { error } = await supabase.auth.exchangeCodeForSession(code);
    // Kein Abbruch bei Fehler: GoTrue `/verify` kann die Sitzung schon angelegt
    // haben, dann scheitert der Tausch und angemeldet ist der Nutzer trotzdem.
    if (error) {
      console.warn("[auth/callback] Code exchange failed, checking existing session:", error.message);
    }
  }

  // Implicit-Flow: `#access_token=…` im Fragment. Der Client kann das NICHT selbst
  // uebernehmen — `createBrowserClient` erzwingt `flowType: "pkce"`, und GoTrue
  // wirft bei dieser Paarung, bevor es irgendetwas tut. Herleitung und Messung
  // stehen in `@janeway/app-sdk/auth/implizite-sitzung`.
  const fragment = new URLSearchParams(rawHash);
  const fragmentToken = fragment.get("access_token");
  if (fragmentToken && fragment.get("refresh_token")) {
    const { data: { session: vorher } } = await supabase.auth.getSession();
    if (!vorher) {
      const abgelaufen = {
        ziel: `${ziele.login}?error=session_failed`,
        fehler: "Der Anmeldelink ist abgelaufen. Bitte fordere einen neuen an.",
      };
      if (!nochGueltig(fragmentToken)) return abgelaufen;
      if (!(await bestaetige({ email: leseToken(fragmentToken).email }))) {
        return { ziel: ziele.login, fehler: null };
      }
      // Die Rueckfrage kann lange offen gestanden haben.
      if (!nochGueltig(fragmentToken)) return abgelaufen;
    }
  }

  const implizit = await uebernimmSitzungAusHash(supabase, rawHash);
  const type = params.get("type") || hashType || implizit.typ;

  // Abgelehnte Uebernahme (anderes Konto angemeldet, ST-22): NICHT mit der alten
  // Sitzung weitermachen. Sonst fuehrte ein Ruecksetzlink auf das Passwortformular
  // des ANGEMELDETEN Kontos, und dessen Passwort wurde geaendert (Prod 2026-10-10).
  if (fragmentToken && !implizit.uebernommen) {
    return {
      ziel: `${ziele.login}?error=session_failed`,
      fehler: implizit.fehler ?? "Authentifizierung fehlgeschlagen",
    };
  }

  const { data: { session } } = await supabase.auth.getSession();

  if (session) {
    if (type === "recovery") return { ziel: "/auth/update-password", fehler: null };
    const ziel = nextParam && isSafeRedirect(nextParam) ? nextParam : ziele.dashboard;
    return { ziel, fehler: null };
  }

  // Weder Code noch Fragment — der Link trug nie etwas zum Anmelden.
  if (!code && !rawHash) return { ziel: `${ziele.login}?error=no_code`, fehler: null };

  // `implizit.fehler` traegt die Ursache von GoTrue. 15 der 16 Kopien haben das
  // Ergebnis weggeworfen und jeden Fall als „fehlgeschlagen" gezeigt.
  return {
    ziel: `${ziele.login}?error=session_failed`,
    fehler: implizit.fehler ?? "Authentifizierung fehlgeschlagen",
  };
}

export interface AuthCallbackProps {
  /** Ziel nach erfolgreicher Anmeldung, wenn kein sicheres `?next=` vorliegt. */
  defaultRedirect?: string;
  /** Wohin bei Fehlern. Default `/auth/login`; admin liegt auf `/login`. */
  loginHref?: string;
}

export function AuthCallback({ defaultRedirect = "/", loginHref = "/auth/login" }: AuthCallbackProps) {
  const router = useRouter();
  const [fehler, setFehler] = useState<string | null>(null);
  const [frage, setFrage] = useState<{ konto: FremdesKonto; antworte: (ja: boolean) => void } | null>(null);
  // Der PKCE-Code ist einmal einloesbar. React montiert im StrictMode zweimal —
  // ohne diesen Riegel scheitert der zweite Tausch und nimmt die eben gewonnene
  // Sitzung mit.
  const gestartet = useRef(false);

  useEffect(() => {
    if (gestartet.current) return;
    gestartet.current = true;

    (async () => {
      const { createSupabaseBrowser } = await import("@janeway/app-sdk/supabase");
      const ergebnis = await loeseAuthCallbackEin(createSupabaseBrowser(), window.location, {
        dashboard: defaultRedirect,
        login: loginHref,
      }, (konto) => new Promise<boolean>((antworte) => setFrage({ konto, antworte })));
      setFrage(null);
      if (!ergebnis.fehler) {
        router.replace(ergebnis.ziel);
        return;
      }
      setFehler(ergebnis.fehler);
      setTimeout(() => router.replace(ergebnis.ziel), 2000);
    })();
  }, [router, defaultRedirect, loginHref]);

  return (
    <div className="flex min-h-[var(--jw-nutzhoehe)] items-center justify-center">
      {fehler ? (
        <ErrorMessage>{fehler}</ErrorMessage>
      ) : frage ? (
        <div className="mx-4 flex max-w-sm flex-col gap-4">
          <h1 className="text-xl font-semibold">Anmelden?</h1>
          <p className="text-sm text-muted-foreground">
            Dieser Link meldet dich als{" "}
            <strong className="text-foreground break-all">{frage.konto.email ?? "unbekanntes Konto"}</strong>{" "}
            an. Fahre nur fort, wenn du ihn selbst angefordert hast.
          </p>
          <div className="flex gap-2">
            <Button className="min-h-11 flex-1" onClick={() => frage.antworte(true)} aria-label="Anmelden">
              Anmelden
            </Button>
            <Button variant="outline" className="min-h-11 flex-1" onClick={() => frage.antworte(false)} aria-label="Abbrechen">
              Abbrechen
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground">Authentifizierung wird verarbeitet...</p>
        </div>
      )}
    </div>
  );
}
